import { Phone, MessageSquare, PartyPopper, Building2, Cake, GraduationCap, Heart, Users, AlertCircle } from "lucide-react";
import { cateringPackages, cateringAddOns, CATERING_PRICES_VERIFIED } from "@/data/catering";
import { IMAGES, SITE } from "@/lib/config";
import { useLang, pick } from "@/lib/i18n";
import { breadcrumbSchema } from "@/lib/schema";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CallLink, TextLink } from "@/components/TrackedLinks";
import { CateringQuoteForm } from "@/components/CateringQuoteForm";

const eventIcons = [PartyPopper, Heart, GraduationCap, Cake, Building2, Users];

export const CateringPackages = () => {
  const { t, lang } = useLang();
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-8">
        {cateringPackages.map((pkg) => (
          <article key={pkg.key} className="bg-hacienda-cream rounded-2xl overflow-hidden border border-hacienda-border-light flex flex-col" data-testid={`catering-package-${pkg.key}`}>
            <div className="aspect-[16/9] overflow-hidden">
              <img src={pkg.image} alt={pick(pkg.title, lang)} loading="lazy" decoding="async" width="800" height="450" className="w-full h-full object-cover" />
            </div>
            <div className="p-7">
              <h3 className="font-serif text-2xl text-hacienda-charcoal font-semibold">{pick(pkg.title, lang)}</h3>
              <p className="text-hacienda-terracotta text-sm mt-1 mb-5">{pick(pkg.subtitle, lang)}</p>
              <ul className="space-y-2.5">
                {pkg.items.map((item) => (
                  <li key={item.qty} className="flex items-center justify-between bg-white p-4 rounded-xl">
                    <div>
                      <span className="font-semibold text-hacienda-charcoal">{item.qty}</span>
                      <span className="text-hacienda-gray text-sm block">{item.consome}</span>
                    </div>
                    <span className="text-hacienda-terracotta font-bold text-xl">{item.price}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-8">
        <div className="bg-hacienda-cream rounded-2xl p-8" data-testid="catering-addons">
          <h3 className="font-serif text-2xl text-hacienda-charcoal font-semibold mb-5">{t("catering.addons")}</h3>
          <ul className="space-y-2.5">
            {cateringAddOns.map((a) => (
              <li key={a.name.en} className="bg-white p-4 rounded-xl flex justify-between items-center">
                <span className="text-hacienda-charcoal">{pick(a.name, lang)}</span>
                <span className="text-hacienda-terracotta font-bold">{pick(a.price, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl p-8 border-2 border-dashed border-hacienda-terracotta bg-hacienda-terracotta/5 flex flex-col justify-center" data-testid="custom-catering">
          <h3 className="font-serif text-2xl text-hacienda-charcoal font-semibold mb-1">{t("catering.mix")}</h3>
          <p className="text-hacienda-terracotta text-sm uppercase tracking-wider mb-4">{t("catering.mixSub")}</p>
          <p className="text-hacienda-gray leading-relaxed">{t("catering.mixCopy")}</p>
          <p className="text-hacienda-gray text-sm mt-4 italic">{t("catering.book")}</p>
        </div>
      </div>

      {!CATERING_PRICES_VERIFIED && (
        <p className="mt-6 flex items-start gap-2 text-sm text-hacienda-gray bg-white border border-hacienda-border-light rounded-xl p-4" data-testid="catering-price-flag">
          <AlertCircle size={16} className="text-hacienda-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" />{t("catering.priceFlag")}
        </p>
      )}
    </div>
  );
};

export const CateringPage = () => {
  const { t } = useLang();
  return (
    <div data-testid="catering-page">
      <Seo
        title="Birria Catering Albuquerque | Tijuas Tacos Birrieria"
        description="Tijuana-style birria catering in Albuquerque: quesabirria taco boxes, tacos dorados, birria by the pound and birria pizza packages for weddings, quinceañeras, graduations, birthdays and office lunches. Request a quote."
        path="/catering"
        jsonLd={breadcrumbSchema([{ label: t("nav.catering"), to: "/catering" }])}
      />
      <Breadcrumbs items={[{ label: t("nav.catering"), to: "/catering" }]} />

      <section className="relative py-20 px-4 overflow-hidden" data-testid="catering-hero">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em] mb-4">{t("catering.eyebrow")}</p>
            <h1 className="font-serif text-5xl md:text-6xl text-hacienda-charcoal font-semibold mb-6 leading-[1.05]">{t("catering.title")}</h1>
            <p className="text-hacienda-gray text-lg leading-relaxed mb-8">{t("catering.sub")}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#quote-form" className="inline-flex items-center justify-center rounded-full bg-hacienda-terracotta hover:bg-hacienda-terracotta-dark text-white px-9 py-4 text-lg font-medium transition-colors" data-testid="catering-quote-anchor">{t("actions.quote")}</a>
              <CallLink location="catering_hero" size="lg" data-testid="catering-call-btn"><Phone size={20} aria-hidden="true" /> {t("actions.call")}</CallLink>
              <TextLink location="catering_hero" size="lg" data-testid="catering-text-btn"><MessageSquare size={20} aria-hidden="true" /> {t("actions.text")}</TextLink>
            </div>
          </div>
          <img src={IMAGES.quesabirriaPlate} alt="Quesabirria tacos plated for a catering order" width="800" height="1066" className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3] lg:aspect-[4/5]" />
        </div>
      </section>

      <section className="py-8 px-4 bg-hacienda-terracotta" data-testid="packages-note">
        <p className="max-w-4xl mx-auto text-center text-white text-lg font-medium">{t("catering.packagesSub")}</p>
      </section>

      <section className="py-20 px-4" data-testid="catering-packages-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-hacienda-charcoal font-semibold mb-10 text-center">{t("catering.packages")}</h2>
          <CateringPackages />
          <div className="mt-12">
            <img src={IMAGES.cateringBoard} alt="Tijuas Tacos Birrieria printed catering packages board with prices" loading="lazy" decoding="async" width="1200" height="1800" className="w-full max-w-3xl mx-auto rounded-2xl shadow-xl" data-testid="catering-full-image" />
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-hacienda-cream" data-testid="event-types-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl text-hacienda-charcoal font-semibold mb-3">{t("catering.events")}</h2>
            <p className="text-hacienda-gray">{t("catering.eventsSub")}</p>
          </div>
          <ul className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {t("catering.eventTypes").slice(0, 6).map((name, i) => {
              const Icon = eventIcons[i];
              return (
                <li key={name} className="bg-white p-6 rounded-2xl border border-hacienda-border-light text-center menu-card" data-testid={`event-type-${i}`}>
                  <div className="bg-hacienda-terracotta/10 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"><Icon size={26} className="text-hacienda-terracotta" aria-hidden="true" /></div>
                  <h3 className="font-semibold text-hacienda-charcoal">{name}</h3>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section id="quote-form" className="py-20 px-4 scroll-mt-24" data-testid="quote-form-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-4xl md:text-5xl text-hacienda-charcoal font-semibold mb-3">{t("catering.formTitle")}</h2>
            <p className="text-hacienda-gray text-lg">{t("catering.formSub")}</p>
          </div>
          <CateringQuoteForm />
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 text-hacienda-gray">
            <CallLink location="catering_form_footer" size="sm" data-testid="catering-bottom-call-btn"><Phone size={16} aria-hidden="true" /> {SITE.phoneDisplay}</CallLink>
            <TextLink location="catering_form_footer" size="sm" data-testid="catering-bottom-text-btn"><MessageSquare size={16} aria-hidden="true" /> {t("actions.text")}</TextLink>
            <a href={`mailto:${SITE.email}`} className="text-sm hover:text-hacienda-terracotta underline underline-offset-4" data-testid="catering-bottom-email-btn">{SITE.email}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CateringPage;
